import styled from '@emotion/styled';
import {
  breakpoints,
  fontSize,
  Modal,
  palette,
  spacing,
  Typography,
} from '@playdapp/ui';

import { Tab, AssetItem } from 'types/merge';

import Button from '@/components/Button';
import Image from '@/components/Image';
import GoogleReCaptcha from '@/components/GoogleReCaptcha';

import { CharacterCount } from '@/components/Merge/style';

type Props = {
  isOpen: boolean;
  selectedTab: Tab;
  selectedTokens: AssetItem[];
  isApproved: boolean;
  recaptcha: string | null;
  handleOpen: (isOpen: boolean) => void;
  handleFetchMerge: () => void;
  handleReCAPTCHA: (token: string | null) => void;
};

const DRAGON_NAME_LIST = [
  'aeormara',
  'brisalid',
  'failaker',
  'hrosveru',
  'palamekid',
  'tyconros',
  'demetalir',
];

const FlexDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const TokenBlock = styled.div`
  width: 100%;
  height: 100%;
  min-height: 164px;
  padding: ${spacing.m};
  border-radius: 0.5rem;
  border: 1px solid ${palette.gray300};
  background-color: ${palette.gray100};
  text-align: center;
`;

const ImageBlock = styled(FlexDisplay)`
  height: auto;
  min-height: 72px;
  margin: ${spacing.s} 2px;
`;

const ImageBox = styled.div`
  position: relative;
`;

const TokenCount = styled(CharacterCount)`
  right: -8px;
  top: -8px;
  width: 1rem;
  height: 1rem;
  font-size: ${fontSize.p5};
`;

const MergeStatusBox = styled.div`
  width: 100%;
  height: 100%;
  margin-top: ${spacing.xl};
  text-align: center;

  ${breakpoints.down('md')} {
    margin-top: ${spacing.l};
  }
`;

const TokenBlockTitle = styled.span`
  font-size: ${fontSize.b4};
  font-weight: 600;
  color: ${palette.dgray300};

  ${breakpoints.down('md')} {
    font-size: ${fontSize.b4};
  }
`;

const StatusTitle = styled.div`
  margin-bottom: ${spacing.s};
  font-size: ${fontSize.h3};
  font-weight: 800;

  ${breakpoints.down('md')} {
    font-size: ${fontSize.h4};
    font-weight: 800;
  }
`;

const StatusDesc = styled.div`
  color: ${palette.gray900};

  p + p {
    margin-top: ${spacing.m};
  }

  ${breakpoints.down('md')} {
    font-size: ${fontSize.b3};
  }
`;

const AgreeButton = styled(Button)`
  margin-top: ${spacing.xxl};

  ${breakpoints.down('md')} {
    margin-top: ${spacing.l};
  }
`;

const MergeConfirmModal = ({
  isOpen,
  selectedTab,
  selectedTokens,
  isApproved,
  recaptcha,
  handleOpen,
  handleFetchMerge,
  handleReCAPTCHA,
}: Props) => {
  return (
    <Modal
      width={{ lg: '500px', xl: '500px' }}
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      handleOpen={handleOpen}
    >
      <ModalContent>
        <TokenBlock>
          <TokenBlockTitle>
            NFT characters to be used for merging
          </TokenBlockTitle>
          <ImageBlock>
            {selectedTokens.map((item, idx) => (
              <ImageBox key={`merge-confirm-modal-image-${idx}`}>
                {DRAGON_NAME_LIST.includes(item.name) &&
                  item.tokens.length > 1 && (
                    <TokenCount>{item.tokens.length}</TokenCount>
                  )}
                <Image
                  prefix="s3"
                  src={`/merge/${item.name}_${
                    selectedTab === 'PLAYDAPP_TOWN_SR' ? 'sr' : 'r'
                  }_success.png`}
                  width="72px"
                  height="72px"
                  alt={item.name}
                  layout="intrinsic"
                />
              </ImageBox>
            ))}
          </ImageBlock>
          <Typography type="b5" color="red">
            NFT characters used for merging will be burned.
          </Typography>
        </TokenBlock>
        <MergeStatusBox>
          <StatusTitle>Merge in progress...</StatusTitle>
          {isApproved && (
            <StatusDesc>
              <p>By merging, your NFTs used for merging will be disabled.</p>
              <p>
                This prompt and wallet information will only appear once during
                the merge process.
              </p>
              <p>
                The detailed information about GAS FEE required for the merge
                will shown in the next step of meta mask approval stage.
              </p>
              <p>please click OK to continue.</p>
            </StatusDesc>
          )}
          {!isApproved && (
            <StatusDesc>
              <span>If all is correct, click OK to continue.</span>
            </StatusDesc>
          )}
        </MergeStatusBox>

        {recaptcha ? (
          <AgreeButton onClick={handleFetchMerge}>OK</AgreeButton>
        ) : (
          <GoogleReCaptcha
            recaptcha={recaptcha}
            handleChange={handleReCAPTCHA}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

export default MergeConfirmModal;
